import { computed, nextTick, watch } from 'vue'
import { idiomaSolicitud, t } from './solicitudTextos'
import { aligerarAdjunto } from './solicitudImagen'
import { useStore } from '../store/index'
import { usePlantilla } from './usePlantilla'
// Respaldo por si la API no responde: el catálogo de verdad (con la documentación de cada notaría) lo da la API
import { CATALOGO_TIPOS as CATALOGO_RESPALDO } from '../data/solicitudWebCatalogo'
import type {
  AdjuntoLocal,
  CitaPropuesta,
  ReglasCitaTramite,
  Interviniente,
  PasoId,
  SolicitudWebEstado,
  SolicitudWebPayload,
  SolicitudWebConfig,
  SolicitudWebRespuesta,
  TipoDocumento,
  TipoOperacionCatalogo
} from '../types/solicitudWeb'

/**
 * Dirección de la API. En producción las webs de las notarías no sirven /api: la API está en el panel de control
 * (igual que el chatbot). En desarrollo se usa la API local (store.api_url).
 */
export function apiSolicitudes(store: any): string {
  return process.env.NODE_ENV === 'development' ? (store.api_url || '/') : 'https://controlpanel.notin.es/'
}

/**
 * Notaría que recibe el encargo cuando no es la propia web (portal agregador, según su plantilla).
 * null = la web actual, como siempre.
 */
export interface DestinoSolicitud {
  id: number
  nombre: string
  /** Grupo de claves de reCAPTCHA de la notaría de destino (la credencial con la que se valida el email). */
  recaptchaGroup: number | null
  /** Próxima fecha para firmar en persona que enseña la notaría en minotaria ("aaaa-mm-dd"), solo para el resumen. */
  proximaFecha?: string | null
}

/**
 * Quien sepa firmar el envío con la clave de reCAPTCHA de otro grupo (la plantilla que ofrece elegir destino).
 * Si no hay proveedor o no hay destino, el asistente usa su token de siempre.
 */
let proveedorTokenDestino: ((grupo: number | null) => Promise<string>) | null = null
export function registrarTokenDestino(fn: ((grupo: number | null) => Promise<string>) | null) {
  proveedorTokenDestino = fn
}
export function hayTokenDestino(): boolean {
  return !!proveedorTokenDestino
}
export function tokenDestino(grupo: number | null): Promise<string> | null {
  return proveedorTokenDestino ? proveedorTokenDestino(grupo) : null
}

const VERSION_ESTADO = 1
/** Versión del texto informativo RGPD mostrado en la revisión. Cambiar si cambia el texto. */
export const VERSION_TEXTO_RGPD = '2026-10'
/** El borrador se descarta pasado este tiempo sin cambios. */
const CADUCIDAD_BORRADOR_MS = 24 * 60 * 60 * 1000
/** Límites de adjuntos (los mismos que comprueba SolicitudWebController en .NET). */
export const ADJUNTOS = {
  maxArchivos: 6,
  // Cada archivo se sube en su propia petición: el proxy de controlpanel admite hasta 6 MB por petición (Fábrica KV1s3sG2)
  maxBytesArchivo: 5 * 1024 * 1024,
  maxBytesTotal: 12 * 1024 * 1024,
  tipos: ['application/pdf', 'image/jpeg', 'image/png'],
  accept: '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'
}

/** Error de envío con un código que el formulario sabe explicar. */
/** Megas para los textos: 3.5, 12. */
export function megas(bytes: number): string {
  return String(Math.round((bytes / 1024 / 1024) * 10) / 10)
}

export class ErrorEnvioSolicitud extends Error {
  constructor(public codigo: string, public estado?: number) {
    super(codigo)
  }
}

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

function nuevoId(): string {
  const c = (globalThis as any).crypto
  if (c?.randomUUID) return c.randomUUID()
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export function nuevaPersona(rol = ''): Interviniente {
  return {
    uid: nuevoId(),
    tipoPersona: 'FISICA',
    nombre: '',
    apellidos: '',
    razonSocial: '',
    representante: { nombre: '', apellidos: '', documento: { tipo: 'DNI', numero: '' } },
    documento: { tipo: 'DNI', numero: '' },
    telefono: '',
    email: '',
    rol,
    intervieneConyuge: false,
    nie: ''
  }
}

/** Canal válido para ?origen= (letras minúsculas y guiones). */
export function canalValido(v: unknown): string | null {
  const c = String(v ?? '').trim().toLowerCase()
  return /^[a-z][a-z-]{0,19}$/.test(c) ? c : null
}

export function estadoInicial(idioma = 'es'): SolicitudWebEstado {
  return {
    version: VERSION_ESTADO,
    claveEnvio: nuevoId(),
    idioma,
    origen: 'formulario',
    paso: 0,
    actualizado: Date.now(),
    presentador: {
      tipo: null,
      profesional: { nombre: '', empresa: '', email: '', telefono: '' }
    },
    tipoOperacion: null,
    solicitante: nuevaPersona(),
    intervinientes: [],
    bienes: [{ descripcion: '', referenciaCatastral: '' }],
    comentario: '',
    contactoPreferido: { medio: '', franja: '' },
    quierePresupuesto: false,
    firmaVideoconferencia: false,
    consentimiento: { aceptado: false, versionTexto: VERSION_TEXTO_RGPD }
  }
}

// ---------------------------------------------------------------------------
// Validación (se repite en el servidor; esto es solo para ayudar al usuario)
// ---------------------------------------------------------------------------

const LETRAS_DNI = 'TRWAGMYFPDXBNJZSQVHLCKE'

export function normalizarDocumento(valor: string): string {
  return (valor || '').toUpperCase().replace(/[\s.\-]/g, '')
}

export function validarDNI(valor: string): boolean {
  const v = normalizarDocumento(valor)
  if (!/^\d{8}[A-Z]$/.test(v)) return false
  return LETRAS_DNI[parseInt(v.slice(0, 8), 10) % 23] === v[8]
}

export function validarNIE(valor: string): boolean {
  const v = normalizarDocumento(valor)
  if (!/^[XYZ]\d{7}[A-Z]$/.test(v)) return false
  const num = 'XYZ'.indexOf(v[0]) + v.slice(1, 8)
  return LETRAS_DNI[parseInt(num, 10) % 23] === v[8]
}

export function validarCIF(valor: string): boolean {
  const v = normalizarDocumento(valor)
  if (!/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(v)) return false
  const digitos = v.slice(1, 8)
  let pares = 0
  let impares = 0
  for (let i = 0; i < 7; i++) {
    const d = parseInt(digitos[i], 10)
    if (i % 2 === 0) {
      const doble = d * 2
      impares += Math.floor(doble / 10) + (doble % 10)
    } else {
      pares += d
    }
  }
  const control = (10 - ((pares + impares) % 10)) % 10
  const letraControl = 'JABCDEFGHI'[control]
  const ultimo = v[8]
  if ('PQRSNW'.includes(v[0])) return ultimo === letraControl
  if ('ABEH'.includes(v[0])) return ultimo === String(control)
  return ultimo === String(control) || ultimo === letraControl
}

export function validarDocumento(tipo: TipoDocumento, numero: string): boolean {
  switch (tipo) {
    case 'DNI': return validarDNI(numero)
    case 'NIE': return validarNIE(numero)
    case 'CIF': return validarCIF(numero)
    case 'PASAPORTE': return /^[A-Z0-9]{5,20}$/.test(normalizarDocumento(numero))
    case 'DOC_UE':
    case 'OTRO': return /^[A-Z0-9]{4,20}$/.test(normalizarDocumento(numero))
    default: return false
  }
}

/** Tipos de documento para personas físicas, en el orden del desplegable. */
/**
 * Documentos con los que se puede firmar en una notaría (los que ofrece el formulario).
 * Los extranjeros se identifican con la tarjeta de residencia (TIE), el pasaporte o, si son de la UE/EEE,
 * con el documento de identidad de su país. El certificado verde de registro UE no sirve (no lleva foto),
 * ni el carnet de conducir ni los documentos de identidad de países de fuera de la UE/EEE.
 */
export const TIPOS_DOCUMENTO: Array<{ value: TipoDocumento; title: string; corto: string; ayuda: string }> = [
  { value: 'DNI', title: 'DNI', corto: 'DNI', ayuda: '8 números y una letra. Ej.: 12345678Z' },
  { value: 'NIE', title: 'TIE / NIE (tarjeta de residencia)', corto: 'NIE', ayuda: 'Número de la tarjeta de residencia: X, Y o Z, 7 números y una letra. Ej.: X1234567L' },
  { value: 'PASAPORTE', title: 'Pasaporte', corto: 'Pasaporte', ayuda: 'Tal como aparece en el pasaporte' },
  { value: 'DOC_UE', title: 'Documento de identidad UE/EEE', corto: 'Doc. UE', ayuda: 'Documento nacional de identidad de un país de la UE o del EEE' }
]

/** Tipos que ya no se ofrecen pero pueden venir de un borrador antiguo. */
const TIPOS_DOCUMENTO_ANTIGUOS = [{ value: 'OTRO' as TipoDocumento, corto: 'Documento' }]

/**
 * true si a esta persona hay que pedirle el NIE aparte: extranjero identificado con pasaporte o documento UE/EEE
 * en un trámite con trascendencia tributaria (el DNI ya es su NIF y la TIE lleva el NIE).
 */
export function necesitaNie(p: Interviniente, tipoOperacion: { pideNie?: boolean } | null | undefined): boolean {
  return p.tipoPersona === 'FISICA' && !!tipoOperacion?.pideNie &&
    (p.documento.tipo === 'PASAPORTE' || p.documento.tipo === 'DOC_UE')
}

export function nombreTipoDocumento(tipo: TipoDocumento): string {
  if (tipo === 'CIF') return 'CIF'
  const d = TIPOS_DOCUMENTO.find((x) => x.value === tipo) ?? TIPOS_DOCUMENTO_ANTIGUOS.find((x) => x.value === tipo)
  return d ? t(d.corto) : tipo
}

/** Si el número tiene forma clara de DNI o NIE, devuelve ese tipo (para corregir el desplegable). */
export function detectarTipoDocumento(numero: string): TipoDocumento | null {
  const v = normalizarDocumento(numero)
  if (/^\d{8}[A-Z]$/.test(v)) return 'DNI'
  if (/^[XYZ]\d{7}[A-Z]$/.test(v)) return 'NIE'
  return null
}

/** Mensaje de error de un documento (o null si es válido). */
export function errorDocumento(tipo: TipoDocumento, numero: string): string | null {
  if (!numero || !numero.trim()) return null
  if (validarDocumento(tipo, numero)) return null
  const v = normalizarDocumento(numero)
  switch (tipo) {
    case 'DNI':
      return /^\d{8}[A-Z]$/.test(v) ? t('La letra no corresponde a ese número de DNI') : t('El DNI son 8 números y una letra')
    case 'NIE':
      return /^[XYZ]\d{7}[A-Z]$/.test(v) ? t('La letra no corresponde a ese NIE') : t('El NIE empieza por X, Y o Z, con 7 números y una letra')
    case 'CIF':
      return /^[A-Z]\d{7}[0-9A-J]$/.test(v) ? t('El dígito de control del CIF no es correcto') : t('El CIF es una letra, 7 números y un carácter de control')
    default:
      return t('Revise el número: solo letras y números')
  }
}

export function validarEmail(valor: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((valor || '').trim())
}

export function validarTelefono(valor: string): boolean {
  const v = (valor || '').replace(/[\s.\-()]/g, '')
  // Móvil/fijo español (9 dígitos) o internacional con prefijo +
  return /^(\+34|0034)?[6789]\d{8}$/.test(v) || /^\+\d{8,15}$/.test(v)
}

const DOMINIOS_COMUNES = ['gmail.com', 'hotmail.com', 'hotmail.es', 'outlook.com', 'outlook.es', 'yahoo.es', 'yahoo.com', 'icloud.com', 'live.com', 'telefonica.net']

function distancia(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)])
  for (let j = 1; j <= b.length; j++) d[0][j] = j
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
  return d[a.length][b.length]
}

/** Mensaje de error del email (o null si es válido). Sugiere el dominio si parece una errata (gmial.com). */
export function errorEmail(valor: string): string | null {
  const v = (valor || '').trim()
  if (!v) return null
  if (/\s/.test(v)) return t('El email no puede tener espacios')
  if (!v.includes('@')) return t('Falta la @ (ej.: nombre@gmail.com)')
  const dominio = v.split('@').pop()!.toLowerCase()
  if (!dominio || !dominio.includes('.')) return t('Falta el dominio después de la @ (ej.: @gmail.com)')
  if (!validarEmail(v)) return t('El email no es válido')
  return null
}

/** Si el dominio parece una errata (gmial.com), devuelve el dominio probable; si no, null. No bloquea el envío. */
export function sugerenciaEmail(valor: string): string | null {
  const v = (valor || '').trim()
  if (!validarEmail(v)) return null
  const dominio = v.split('@').pop()!.toLowerCase()
  if (DOMINIOS_COMUNES.includes(dominio)) return null
  return DOMINIOS_COMUNES.find((d) => distancia(d, dominio) <= 2) ?? null
}

/** Mensaje de error del teléfono (o null si es válido). */
export function errorTelefono(valor: string): string | null {
  const v = (valor || '').replace(/[\s.\-()]/g, '')
  if (!v) return null
  if (validarTelefono(v)) return null
  if (/[^\d+]/.test(v)) return t('Solo números (y + para el prefijo de otro país)')
  if (v.startsWith('+') || v.startsWith('00')) return t('Revise el número con prefijo: entre 8 y 15 cifras')
  if (v.length !== 9) return t('Un teléfono español tiene 9 cifras (ha escrito {n})', { n: v.length })
  return t('Los teléfonos españoles empiezan por 6, 7, 8 o 9')
}

type Errores = Record<string, string>

function validarPersona(
  p: Interviniente,
  prefijo: string,
  opciones: { contactoObligatorio: boolean; rolObligatorio: boolean },
  e: Errores
) {
  if (p.tipoPersona === 'FISICA') {
    if (!p.nombre.trim()) e[`${prefijo}.nombre`] = t('Indique el nombre')
    if (!p.apellidos.trim()) e[`${prefijo}.apellidos`] = t('Indique los apellidos')
    if (!p.documento.numero.trim()) e[`${prefijo}.documento`] = t('Indique el número de documento')
    else if (!validarDocumento(p.documento.tipo, p.documento.numero))
      e[`${prefijo}.documento`] = errorDocumento(p.documento.tipo, p.documento.numero) ?? t('El documento no es válido')
    // El NIE aparte es opcional (puede que aún no lo tenga), pero si lo escribe tiene que ser correcto
    if ((p.documento.tipo === 'PASAPORTE' || p.documento.tipo === 'DOC_UE') && p.nie?.trim() && !validarNIE(p.nie))
      e[`${prefijo}.nie`] = errorDocumento('NIE', p.nie) ?? t('El documento no es válido')
  } else {
    if (!p.razonSocial.trim()) e[`${prefijo}.razonSocial`] = t('Indique la razón social')
    if (!p.documento.numero.trim()) e[`${prefijo}.documento`] = t('Indique el CIF')
    else if (!validarCIF(p.documento.numero)) e[`${prefijo}.documento`] = errorDocumento('CIF', p.documento.numero) ?? t('El CIF no es válido')
    const r = p.representante
    if (!r.nombre.trim()) e[`${prefijo}.representante.nombre`] = t('Indique el nombre del representante')
    if (!r.apellidos.trim()) e[`${prefijo}.representante.apellidos`] = t('Indique los apellidos del representante')
    if (r.documento.numero.trim() && !validarDocumento(r.documento.tipo, r.documento.numero))
      e[`${prefijo}.representante.documento`] = t('El documento no es válido')
  }
  if (opciones.contactoObligatorio) {
    if (!p.telefono.trim()) e[`${prefijo}.telefono`] = t('Indique un teléfono')
    if (!p.email.trim()) e[`${prefijo}.email`] = t('Indique un email')
  }
  const errTel = errorTelefono(p.telefono)
  if (errTel) e[`${prefijo}.telefono`] = errTel
  const errMail = errorEmail(p.email)
  if (errMail) e[`${prefijo}.email`] = errMail
  if (opciones.rolObligatorio && !p.rol) e[`${prefijo}.rol`] = t('Indique su papel en la operación')
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export function useSolicitudWeb() {
  const store = useStore()
  const estado = useState<SolicitudWebEstado>('solicitudWeb', () => estadoInicial(store.idioma || 'es'))
  const errores = useState<Errores>('solicitudWebErrores', () => ({}))
  // Los archivos no se pueden guardar en el borrador: viven solo mientras la página está abierta
  const adjuntos = useState<AdjuntoLocal[]>('solicitudWebAdjuntos', () => [])

  /**
   * Configuración pública que devuelve la API con el catálogo. Es la única fuente fiable: la configuración es
   * común a todos los idiomas, mientras que store.NOTARIA es la copia del idioma de la web (puede ir por detrás).
   */
  const configApi = useState<Partial<SolicitudWebConfig> | null>('solicitudWebConfigApi', () => null)

  const config = computed<SolicitudWebConfig>(() => ({
    Activa: false,
    Modo: 'EMAIL',
    EnviarCopiaCliente: true,
    PermitirAdjuntos: true,
    ...(store.NOTARIA?.SolicitudWeb ?? {}),
    ...(configApi.value ?? {})
  }))

  // Lo que la plantilla de la web añade al encargo (paso de destino, canal, textos). Sin plantilla, nada.
  const { plantilla } = usePlantilla()
  const encargoPlantilla = computed(() => plantilla.value.encargo ?? null)
  const destino = useState<DestinoSolicitud | null>('solicitudWebDestino', () => null)
  /** true cuando el cliente ya ha elegido notaría o "sin preferencia" en el paso de destino. */
  const destinoElegido = useState<boolean>('solicitudWebDestinoElegido', () => false)

  /** La web en la que está el formulario (borrador, estadísticas, invitaciones). */
  const idWeb = computed<number | null>(() => store.NOTARIA?.id ?? store.NOTARIA?.IdValue ?? null)
  /** La notaría que recibe el encargo: el destino elegido o la propia web. */
  const idNotaria = computed<number | null>(() => destino.value?.id ?? idWeb.value)
  const claveStorage = computed(() => `solicitud-web-${idWeb.value ?? 'x'}`)

  /** La NV solo existe para el formulario si la notaría tiene el modo "Directamente a la Notaría Virtual". */
  const conNV = computed(() => config.value.Modo === 'NV')

  // Catálogo de trámites de la notaría (se carga de la API al abrir el formulario)
  const catalogo = useState<TipoOperacionCatalogo[] | null>('solicitudWebCatalogo', () => null)
  const estadoCatalogo = useState<'pendiente' | 'cargando' | 'listo' | 'respaldo'>('solicitudWebEstadoCatalogo', () => 'pendiente')
  const tipos = computed<TipoOperacionCatalogo[]>(() => {
    const lista = catalogo.value ?? []
    // El catálogo de la API ya viene traducido; el de respaldo (en español) se traduce aquí
    if (estadoCatalogo.value !== 'respaldo' || idiomaSolicitud.value === 'es') return lista
    return lista.map((op) => ({
      ...op,
      nombre: t(op.nombre),
      descripcion: t(op.descripcion),
      roles: op.roles.map((r) => ({ ...r, nombre: t(r.nombre) })),
      documentos: op.documentos.map((d) => ({ ...d, nombre: t(d.nombre), nombreEs: d.nombreEs ?? d.nombre }))
    }))
  })

  async function cargarCatalogo() {
    // Solo se pide una vez (también si se usó el de respaldo): volver a pedirlo al montar el asistente
    // hacía que la página alternara entre "cargando" y el formulario sin parar cuando la API no responde.
    // Para recargarlo (cambio de idioma) se pone antes en 'pendiente'.
    if (estadoCatalogo.value !== 'pendiente') return
    estadoCatalogo.value = 'cargando'
    try {
      const res = await $fetch<{ tipos: TipoOperacionCatalogo[]; config?: Partial<SolicitudWebConfig> }>(
        `${apiSolicitudes(store)}api/SolicitudWeb/Catalogo/${idNotaria.value}?idioma=${encodeURIComponent(idiomaSolicitud.value)}`,
        { retry: 1, timeout: 10000 })
      if (!res?.tipos?.length) throw new Error('Catálogo vacío')
      catalogo.value = res.tipos
      if (res.config) configApi.value = res.config
      estadoCatalogo.value = 'listo'
    } catch (e) {
      console.warn('[SolicitudWeb] No se pudo cargar el catálogo de la API; se usa el de respaldo', e)
      catalogo.value = CATALOGO_RESPALDO
      estadoCatalogo.value = 'respaldo'
    }
  }

  const tipo = computed(() => tipos.value.find((x) => x.codigo === estado.value.tipoOperacion) ?? null)

  /**
   * Cita de firma: reglas de la notaría para el trámite elegido, o null si no la ofrece. No se ofrece en un portal
   * agregador (minotaria): allí aún no hay notaría cuyas reglas aplicar. Ref: https://trello.com/c/Bnl5pAM2
   */
  const reglasCita = computed<ReglasCitaTramite | null>(() => {
    const c = config.value.Cita
    const tr = estado.value.tipoOperacion ? c?.tramites?.[estado.value.tipoOperacion] : null
    if (!c || !tr || encargoPlantilla.value) return null
    return { ...tr, diasSemana: c.diasSemana ?? [], diasCerrados: c.diasCerrados ?? [], horas: c.horas ?? [] }
  })
  /** Aviso que acompaña al calendario: el de la notaría o el predeterminado. */
  const avisoCita = computed(() => config.value.Cita?.texto ||
    t('La fecha y la hora son orientativas. La notaría se pondrá en contacto con usted para confirmarlas.'))

  /** La cita elegida, si sigue cumpliendo las reglas del trámite (cambiar de trámite puede dejarla fuera). */
  function citaValida(c: CitaPropuesta | null | undefined, r: ReglasCitaTramite | null): CitaPropuesta | null {
    if (!c?.fecha || !r) return null
    if (c.fecha < r.desde || c.fecha > r.hasta || r.diasCerrados.includes(c.fecha)) return null
    const f = new Date(c.fecha + 'T12:00:00')
    if (!r.diasSemana.includes(f.getDay() === 0 ? 7 : f.getDay())) return null
    if (r.modo === 'DIA') return { fecha: c.fecha, hora: '' }
    return { fecha: c.fecha, hora: r.horas.includes(c.hora) ? c.hora : '' }
  }

  /** Nombre visible (en el idioma de la web) de un documento guardado con su nombre en español. */
  function nombreDocumento(nombreEs: string): string {
    if (nombreEs === 'Otro documento') return t('Otro documento')
    return tipo.value?.documentos.find((d) => (d.nombreEs || d.nombre) === nombreEs)?.nombre ?? nombreEs
  }

  /** Al cambiar el idioma de la web se recarga el catálogo traducido. */
  function vigilarIdioma() {
    watch(idiomaSolicitud, () => {
      estadoCatalogo.value = 'pendiente'
      cargarCatalogo()
    })
  }
  const esProfesional = computed(() => estado.value.presentador.tipo === 'PROFESIONAL')

  const pasos = computed<PasoId[]>(() => {
    // Primero el trámite: es lo que el cliente viene a hacer y lo que más le orienta
    const p: PasoId[] = ['tipo', 'presentador', 'solicitante']
    // La plantilla puede pedir elegir la notaría de destino justo después del trámite
    if (encargoPlantilla.value?.pasoDestino) p.splice(1, 0, 'destino')
    if (!tipo.value || tipo.value.permiteOtrosIntervinientes) p.push('intervinientes')
    p.push('detalle', 'revision')
    return p
  })
  const indice = computed(() => Math.min(estado.value.paso, pasos.value.length - 1))
  const pasoActual = computed<PasoId>(() => pasos.value[indice.value])

  // --- Borrador (localStorage) ----------------------------------------------
  // Se guarda en localStorage para no perder lo escrito si cierra la pestaña o recarga.
  // Como contiene datos personales: caduca a las 24 h y se borra en cuanto el encargo se envía.

  function cargarBorrador() {
    if (!process.client) return
    try {
      // Borradores de antes, en sessionStorage: se pasan a localStorage
      const antiguo = sessionStorage.getItem(claveStorage.value)
      if (antiguo) {
        sessionStorage.removeItem(claveStorage.value)
        if (!localStorage.getItem(claveStorage.value)) localStorage.setItem(claveStorage.value, antiguo)
      }
      const raw = localStorage.getItem(claveStorage.value)
      if (!raw) return
      const b = JSON.parse(raw) as SolicitudWebEstado
      if (b.version !== VERSION_ESTADO || Date.now() - b.actualizado > CADUCIDAD_BORRADOR_MS) {
        localStorage.removeItem(claveStorage.value)
        return
      }
      estado.value = b
    } catch {
      /* borrador corrupto o storage bloqueado: se empieza de cero */
    }
  }

  function guardarBorrador() {
    if (!process.client) return
    try {
      estado.value.actualizado = Date.now()
      localStorage.setItem(claveStorage.value, JSON.stringify(estado.value))
    } catch {
      /* storage no disponible: el formulario funciona igual sin guardar */
    }
  }

  /** Guardado pendiente (el autoguardado espera 400 ms): se anula al borrar para que no resucite el borrador. */
  let guardadoPendiente: ReturnType<typeof setTimeout> | null = null

  function borrarBorrador() {
    if (!process.client) return
    if (guardadoPendiente) { clearTimeout(guardadoPendiente); guardadoPendiente = null }
    try { localStorage.removeItem(claveStorage.value) } catch { /* nada */ }
    try { sessionStorage.removeItem(claveStorage.value) } catch { /* nada */ }
  }

  /**
   * Llamar UNA vez desde el contenedor del formulario.
   * `pausado`: mientras devuelva true no se guarda (p. ej. con el encargo ya enviado, para que al volver no reaparezca).
   */
  function activarAutoguardado(pausado: () => boolean = () => false) {
    cargarBorrador()
    watch(estado, () => {
      if (guardadoPendiente) clearTimeout(guardadoPendiente)
      if (pausado()) { guardadoPendiente = null; return }
      guardadoPendiente = setTimeout(() => { guardadoPendiente = null; if (!pausado()) guardarBorrador() }, 400)
    }, { deep: true })
  }

  // --- Validación por paso ---------------------------------------------------

  function validarPaso(id: PasoId): Errores {
    const e: Errores = {}
    const s = estado.value
    switch (id) {
      case 'presentador': {
        if (!s.presentador.tipo) e['presentador.tipo'] = t('Elija una opción')
        if (s.presentador.tipo === 'PROFESIONAL') {
          const pr = s.presentador.profesional
          if (!pr.nombre.trim()) e['profesional.nombre'] = t('Indique su nombre')
          if (!pr.email.trim()) e['profesional.email'] = t('Indique un email')
          else if (!validarEmail(pr.email)) e['profesional.email'] = errorEmail(pr.email) ?? t('El email no es válido')
          if (!pr.telefono.trim()) e['profesional.telefono'] = t('Indique un teléfono')
          else if (!validarTelefono(pr.telefono)) e['profesional.telefono'] = errorTelefono(pr.telefono) ?? t('El teléfono no es válido')
        }
        break
      }
      case 'tipo':
        if (!s.tipoOperacion) e['tipoOperacion'] = t('Elija el encargo que necesita')
        break
      case 'destino':
        if (!destinoElegido.value) e['destino'] = t('Elija una notaría o indique que no tiene preferencia')
        break
      case 'solicitante':
        validarPersona(s.solicitante, 'solicitante', {
          contactoObligatorio: !esProfesional.value,
          rolObligatorio: (tipo.value?.roles.length ?? 0) > 1
        }, e)
        break
      case 'intervinientes':
        s.intervinientes.forEach((p, i) =>
          validarPersona(p, `intervinientes.${i}`, { contactoObligatorio: false, rolObligatorio: (tipo.value?.roles.length ?? 0) > 1 }, e))
        break
      case 'detalle': {
        if (s.comentario.length > 2000) e['comentario'] = t('Máximo 2000 caracteres')
        const cita = citaValida(s.cita, reglasCita.value)
        if (s.cita?.fecha && reglasCita.value && !cita) e['cita'] = t('Esa fecha ya no está disponible. Elija otra.')
        else if (cita && reglasCita.value?.modo === 'DIA_HORA' && !cita.hora) e['cita'] = t('Elija también la hora de la cita.')
        break
      }
      case 'revision':
        if (!s.consentimiento.aceptado) e['consentimiento'] = t('Debe aceptar la política de privacidad para enviar')
        break
    }
    return e
  }

  /** Quita el error de un campo en cuanto el usuario lo corrige. */
  function limpiarError(clave: string) {
    if (errores.value[clave]) {
      const { [clave]: _, ...resto } = errores.value
      errores.value = resto
    }
  }

  function irArriba() {
    if (process.client) window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  /** Lleva al primer error del paso: si queda más arriba (p. ej. la hora de la cita), el visitante no lo vería al pulsar "Continuar". */
  function irAlPrimerError() {
    if (!process.client) return
    nextTick(() => {
      const el = document.querySelector('.sw .sw-error, .sw .v-input--error') as HTMLElement | null
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  function siguiente(): boolean {
    const e = validarPaso(pasoActual.value)
    errores.value = e
    if (Object.keys(e).length) {
      irAlPrimerError()
      return false
    }
    if (indice.value < pasos.value.length - 1) {
      estado.value.paso = indice.value + 1
      irArriba()
    }
    return true
  }

  function anterior() {
    errores.value = {}
    if (indice.value > 0) {
      estado.value.paso = indice.value - 1
      irArriba()
    }
  }

  function irAPaso(id: PasoId) {
    const i = pasos.value.indexOf(id)
    if (i >= 0) {
      errores.value = {}
      estado.value.paso = i
      irArriba()
    }
  }

  /**
   * Si se puede pedir firma por videoconferencia en el trámite: lo admite y la notaría la ofrece. En una plantilla que elige
   * la notaría después (minotaria) se ofrece siempre: el paso de destino solo lista las que la hacen. Ref: https://trello.com/c/dc7kKk4R
   */
  function videoconferenciaEn(codigo: string | null): boolean {
    const t = codigo ? tipos.value.find((x) => x.codigo === codigo) : null
    return !!t?.admiteVideoconferencia && (!!config.value.OfreceVideoconferencia || !!encargoPlantilla.value?.pasoDestino)
  }
  /** La pregunta "¿Cómo prefiere firmar?" va en el detalle, salvo en la plantilla con paso de destino, que la hace allí. */
  const preguntarFirma = computed(() => !encargoPlantilla.value?.pasoDestino && videoconferenciaEn(estado.value.tipoOperacion))

  function elegirTipo(codigo: string) {
    // Otro trámite: la notaría elegida puede no atenderlo, así que se vuelve a elegir
    if (estado.value.tipoOperacion !== codigo && destinoElegido.value) elegirDestino(null, false)
    if (!videoconferenciaEn(codigo)) estado.value.firmaVideoconferencia = false
    // En la plantilla que elige notaría (minotaria), la videoconferencia va primero: marcada al elegir un trámite que la admite.
    // Ref: https://trello.com/c/pooUEbKK
    else if (encargoPlantilla.value?.pasoDestino && estado.value.tipoOperacion !== codigo) estado.value.firmaVideoconferencia = true
    estado.value.tipoOperacion = codigo
    const t = tipos.value.find((x) => x.codigo === codigo)
    if (!t) return
    const rolesValidos = t.roles.map((r) => r.codigo)
    if (!rolesValidos.includes(estado.value.solicitante.rol)) estado.value.solicitante.rol = t.rolSolicitantePorDefecto
    estado.value.intervinientes.forEach((p) => { if (!rolesValidos.includes(p.rol)) p.rol = '' })
    if (!t.permiteOtrosIntervinientes) estado.value.intervinientes = []
  }

  /**
   * Notaría que recibe el encargo (null = la propia web). Se recarga el catálogo y la configuración
   * de esa notaría: su documentación y sus opciones son las que valen.
   */
  function elegirDestino(d: DestinoSolicitud | null, elegido = true) {
    const cambia = (destino.value?.id ?? null) !== (d?.id ?? null)
    destino.value = d
    destinoElegido.value = elegido
    if (elegido) limpiarError('destino')
    if (cambia) {
      configApi.value = null
      estadoCatalogo.value = 'pendiente'
      cargarCatalogo()
    }
  }

  function anadirInterviniente() {
    estado.value.intervinientes.push(nuevaPersona(''))
  }

  function quitarInterviniente(uid: string) {
    estado.value.intervinientes = estado.value.intervinientes.filter((p) => p.uid !== uid)
  }

  function nombreRol(codigo: string): string {
    return tipo.value?.roles.find((r) => r.codigo === codigo)?.nombre ?? codigo
  }

  // --- Adjuntos --------------------------------------------------------------

  /** Añade archivos. Devuelve un mensaje si alguno no se ha podido añadir. */
  /** Añade archivos a un documento. Las fotos grandes y los PDF que pasan del límite se reducen antes de comprobar el tamaño. */
  async function anadirAdjuntos(files: FileList | File[], documento: string): Promise<string | null> {
    const lista = Array.from(files)
    const avisos: string[] = []
    for (const original of lista) {
      const f = await aligerarAdjunto(original, ADJUNTOS.maxBytesArchivo)
      if (adjuntos.value.length >= ADJUNTOS.maxArchivos) {
        avisos.push(t('Como máximo {n} archivos', { n: ADJUNTOS.maxArchivos }))
        break
      }
      const tipoOk = ADJUNTOS.tipos.includes(f.type) || /\.(pdf|jpe?g|png)$/i.test(f.name)
      if (!tipoOk) { avisos.push(`${f.name}: ${t('solo PDF, JPG o PNG')}`); continue }
      if (f.size === 0) { avisos.push(`${f.name}: ${t('el archivo está vacío')}`); continue }
      if (f.size > ADJUNTOS.maxBytesArchivo) { avisos.push(`${f.name}: ${t('No se ha podido reducir este archivo por debajo de {limite} MB, que es el máximo por archivo. Pruebe a enviarlo en varias partes.', { limite: megas(ADJUNTOS.maxBytesArchivo) })}`); continue }
      const total = adjuntos.value.reduce((s, a) => s + a.file.size, 0) + f.size
      if (total > ADJUNTOS.maxBytesTotal) { avisos.push(`${f.name}: ${t('entre todos los archivos superan los {limite} MB', { limite: megas(ADJUNTOS.maxBytesTotal) })}`); continue }
     const base64data = await leerArchivoBase64(f);
      adjuntos.value.push({
      uid: nuevoId(),
      file: f,
      documento,
      contenido: base64data
    });
    }
    return avisos.length ? avisos.join('. ') : null
  }
  const leerArchivoBase64 = (f: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve((reader.result as string).split(',')[1]);
      } else {
        reject(new Error('No se pudo convertir el archivo a Base64'));
      }
    };

    reader.onerror = () => {
      reject(reader.error ?? new Error('Error al leer el archivo'));
    };

    reader.readAsDataURL(f);
  });
};
  function quitarAdjunto(uid: string) {
    adjuntos.value = adjuntos.value.filter((a) => a.uid !== uid)
  }

  /** Sube (-1) o baja (+1) una foto dentro de su documento. La API las une en un PDF en este orden. */
  function moverAdjunto(uid: string, delta: -1 | 1) {
    const lista = adjuntos.value
    const i = lista.findIndex((a) => a.uid === uid)
    if (i < 0) return
    let j = i + delta
    while (j >= 0 && j < lista.length && lista[j].documento !== lista[i].documento) j += delta
    if (j < 0 || j >= lista.length) return
    const copia = [...lista]
    ;[copia[i], copia[j]] = [copia[j], copia[i]]
    adjuntos.value = copia
  }

  // --- Envío -----------------------------------------------------------------

  function limpiarPersona(p: Interviniente, esSolicitante: boolean) {
    const { uid, ...resto } = p
    return {
      ...resto,
      rolNombre: p.rol ? nombreRol(p.rol) : '',
      documento: { ...p.documento, numero: normalizarDocumento(p.documento.numero) },
      nie: necesitaNie(p, tipo.value) ? normalizarDocumento(p.nie ?? '') : '',
      representante: p.tipoPersona === 'JURIDICA'
        ? { ...p.representante, documento: { ...p.representante.documento, numero: normalizarDocumento(p.representante.documento.numero) } }
        : null,
      email: p.email.trim(),
      telefono: p.telefono.trim(),
      esSolicitante
    }
  }

  /** Prefijo de la web para el idioma del formulario: el idioma principal no lleva prefijo. */
  function prefijoIdioma(): string {
    const principal = (store.languages ?? []).find((l: any) => l?.Language)?.Language ?? 'es'
    return idiomaSolicitud.value === principal ? '' : '/' + idiomaSolicitud.value
  }

  function construirPayload(recaptchaToken: string, honeypot: string, tokenReintento = ''): SolicitudWebPayload {
    const s = estado.value
    return {
      claveEnvio: s.claveEnvio,
      idioma: idiomaSolicitud.value,
      origen: s.origen,
      canal: encargoPlantilla.value?.canal || s.canal || 'web',
      tipoOperacion: s.tipoOperacion as string,
      tipoOperacionNombre: tipo.value?.nombre ?? (s.tipoOperacion as string),
      presentador: {
        tipo: s.presentador.tipo!,
        profesional: s.presentador.tipo === 'PROFESIONAL' ? { ...s.presentador.profesional } : null
      },
      intervinientes: [limpiarPersona(s.solicitante, true), ...s.intervinientes.map((p) => limpiarPersona(p, false))],
      bienes: tipo.value?.llevaBienes ? s.bienes.filter((b) => b.descripcion.trim() || b.referenciaCatastral.trim()) : [],
      comentario: s.comentario.trim(),
      contactoPreferido: { medio: s.contactoPreferido?.medio ?? '', franja: s.contactoPreferido?.franja ?? '' },
      quierePresupuesto: !!config.value.PermitirPresupuesto && !!s.quierePresupuesto,
      cita: citaValida(s.cita, reglasCita.value),
      firmaVideoconferencia: videoconferenciaEn(s.tipoOperacion) && !!s.firmaVideoconferencia,
      prefijoIdioma: prefijoIdioma(),
      invitacion: s.invitacion || '',
      adjuntos: config.value.PermitirAdjuntos ? adjuntos.value.map((a) => ({ Documento: a.documento, Contenido: a.contenido, nombre: a.file.name })) : [],
      consentimiento: { ...s.consentimiento },
      website: honeypot,
      idWebOrigen: encargoPlantilla.value ? idWeb.value : null,
      // Datos propios de la plantilla (p. ej. la zona en minotaria.es). Sin plantilla, nada.
      ...(encargoPlantilla.value?.datosExtra?.() ?? {}),
      recaptcha: {
        id_credential: destino.value ? destino.value.recaptchaGroup : (store.NOTARIA?.Personalizar?.RecaptchaGroup || (hayTokenDestino() ? 1 : null)),
        token: recaptchaToken,
        // Por si la API tiene que reenviar el email sin adjuntos (cada token vale para un envío)
        tokenReintento,
        // Misma acción que el formulario de contacto: la valida el servicio de correo
        action: 'email'
      }
    }
  }

  /** Valida todos los pasos. Devuelve el primer paso con errores o null. */
  function validarTodo(): PasoId | null {
    for (const id of pasos.value) {
      const e = validarPaso(id)
      if (Object.keys(e).length) {
        errores.value = e
        return id
      }
    }
    errores.value = {}
    return null
  }

  /** Texto para el cuerpo HTML del correo. */
  function esc(v: unknown): string {
    return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  }

  /**
   * Respaldo si la API no responde: manda la solicitud (sin documentos) a la notaría por el mismo servicio de
   * correo y con el mismo formato que el formulario de contacto (FormNotinOne.vue). No añadir claves nuevas:
   * el servicio de correo es delicado con los parámetros que recibe.
   */
  async function enviarPorCorreo(payload: SolicitudWebPayload, token: string): Promise<SolicitudWebRespuesta> {
    const destino = store.NOTARIA?.Contacto?.Email
    if (!destino || !token) throw new ErrorEnvioSolicitud('ERROR')

    const nombrePersona = (p: any) => (p.tipoPersona === 'JURIDICA' ? p.razonSocial : `${p.nombre} ${p.apellidos}`).trim()
    const fila = (etiqueta: string, valor: unknown) => (valor ? `<b>${esc(etiqueta)}:</b> ${esc(valor)}<br>` : '')
    const referencia = 'WEB-' + new Date().toISOString().slice(2, 10).replace(/-/g, '') + '-' +
      String(payload.claveEnvio).replace(/[^A-Za-z0-9]/g, '').slice(0, 5).toUpperCase()
    const sol: any = payload.intervinientes[0]
    const nombreSol = nombrePersona(sol)

    let cuerpo = `Encargo online enviado desde la web: ${esc(location.origin)}<br><br>`
    cuerpo += '<b>ATENCIÓN:</b> Esta solicitud ha llegado por el formulario de respaldo, sin pasar por la gestión de encargos del panel (queda en la bandeja de Emails). No lleva documentos adjuntos: pídaselos al cliente al contactarle.<br><br>'
    cuerpo += fila('Referencia', referencia) + fila('Trámite', payload.tipoOperacionNombre) + '<br>'
    cuerpo += `<b>Solicitante</b><br>${fila('Nombre', nombreSol)}${fila('Documento', `${sol.documento.tipo} ${sol.documento.numero}`)}${fila('Correo de contacto', sol.email)}${fila('Teléfono', sol.telefono)}<br>`
    if (payload.presentador.tipo === 'PROFESIONAL' && payload.presentador.profesional) {
      const pr: any = payload.presentador.profesional
      cuerpo += `<b>Presentado por un profesional</b><br>${fila('Nombre', pr.nombre)}${fila('Empresa', pr.empresa)}${fila('Correo', pr.email)}${fila('Teléfono', pr.telefono)}<br>`
    }
    for (const p of payload.intervinientes.slice(1) as any[]) {
      cuerpo += `<b>${esc(p.rolNombre || 'Interviniente')}</b><br>${fila('Nombre', nombrePersona(p))}${fila('Documento', `${p.documento.tipo} ${p.documento.numero}`)}${fila('Correo', p.email)}${fila('Teléfono', p.telefono)}<br>`
    }
    for (const b of payload.bienes) {
      cuerpo += `<b>Bien</b><br>${fila('Descripción', b.descripcion)}${fila('Ref. catastral', b.referenciaCatastral)}<br>`
    }
    cuerpo += fila('Comentario', payload.comentario)
    // Los archivos no viajan por este camino: se dice cuáles quiso adjuntar para que la notaría los pida
    const docs = config.value.PermitirAdjuntos ? adjuntos.value : []
    if (docs.length) {
      cuerpo += '<br><b>Documentos que el cliente quiso adjuntar (NO se han recibido):</b><br>' +
        docs.map((d) => [nombreDocumento(d.documento), d.file.name].filter(Boolean).map(esc).join(': ')).join('<br>') + '<br><br>'
    }
    cuerpo += fila('Contacto preferido', [payload.contactoPreferido.medio, payload.contactoPreferido.franja].filter(Boolean).join(' · '))
    if (payload.quierePresupuesto) cuerpo += '<b>Desea presupuesto previo.</b><br>'
    if (payload.cita) {
      const [a, m, d] = payload.cita.fecha.split('-')
      cuerpo += fila('Cita propuesta (pendiente de confirmar)', `${d}/${m}/${a}` + (payload.cita.hora ? ' ' + payload.cita.hora : ''))
    }
    if (payload.firmaVideoconferencia) cuerpo += '<b>Prefiere firmar por videoconferencia.</b><br>'
    cuerpo += '<br><br><br><b>Por favor, no responda a este correo. Este mensaje ha sido generado automáticamente y no está configurado para recibir respuestas.</b>'

    const asunto = `Encargo online WEB - ${store.NOTARIA.Nombre} - ${payload.tipoOperacionNombre} - ${nombreSol} - ${referencia}`
    const modelo = {
      perfil: 'info_recaptcha_web_notaria',
      cuerpo,
      is_cuerpo_html: true,
      asunto,
      destinatarios: [{ nombre: store.NOTARIA.Nombre, direccion: destino }],
      destinatarios_cco: [
        { nombre: 'webNotin', direccion: 'web@notin.net' },
        { nombre: 'avisosUnidata', direccion: 'pwavisos@unidata.es' }
      ],
      recaptcha: { id_credential: payload.recaptcha.id_credential, token, action: 'email' },
      data: {
        id_notaria: store.NOTARIA.id,
        Fecha: new Date().getTime().toString(),
        Name: nombreSol,
        Email: sol.email,
        Phone: sol.telefono,
        Subject: asunto,
        Message: `${payload.tipoOperacionNombre}. ${payload.comentario}`.slice(0, 500)
      }
    }
    try {
      await $fetch('https://mailing.notin.es/api/email', { method: 'POST', body: modelo, headers: { 'Content-Type': 'application/json' } })
    } catch (e) {
      throw new ErrorEnvioSolicitud('ERROR')
    }

    // El correo ha salido: la solicitud queda también en la bandeja de Emails del panel (la misma ruta pública que
    // usa el formulario de contacto). Si falla no pasa nada: la notaría ya la tiene en su correo.
    try {
      const texto = cuerpo
        .replace(/<br>/g, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&quot;/g, '"').replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/&amp;/g, '&')
        .replace(/Por favor, no responda[\s\S]*$/, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
      const emailPanel = { Subject: asunto, Name: nombreSol, Email: sol.email, Phone: sol.telefono, Message: texto.slice(0, 4000) }
      const bodystr = btoa(unescape(encodeURIComponent(JSON.stringify(emailPanel))))
      const idN = store.NOTARIA?.id || store.NOTARIA?.IdValue
      if (idN) {
        const apiBase = (store.backend_api_url || (process.env.NODE_ENV === 'development' ? store.api_url : 'https://controlpanel.notin.es/')).replace(/\/+$/, '')
        $fetch(`${apiBase}/api/Emails/Public/${idN}`, { method: 'POST', body: { bodystr } })
          .catch((e) => console.error('[SolicitudWeb] No se ha podido guardar en la bandeja del panel', e))
      }
    } catch (e) {
      console.error('[SolicitudWeb] No se ha podido guardar en la bandeja del panel', e)
    }
    return { referencia, enlace: null, respaldo: true, documentosPendientes: docs.length }
  }

  /**
   * Sube los archivos de uno en uno (mismo claveEnvio que el encargo). Cada uno guarda el identificador que devuelve
   * la API, así que si el envío falla y se reintenta no se vuelven a subir.
   */
  async function subirAdjuntos() {
    const url = `${apiSolicitudes(store)}api/SolicitudWeb/Public/${idNotaria.value}/adjunto`
    for (const a of adjuntos.value) {
      if (a.idSubida) continue
      const form = new FormData()
      form.append('claveEnvio', estado.value.claveEnvio)
      form.append('archivo', a.file, a.file.name)
      const r = await $fetch<{ id: string }>(url, { method: 'POST', body: form })
      a.idSubida = r.id
    }
  }

  async function enviar(recaptchaToken: string, honeypot = '', tokenReintento = ''): Promise<SolicitudWebRespuesta> {
    const payload = construirPayload(recaptchaToken, honeypot, tokenReintento)

    try {
      debugger
      // Los archivos van antes, uno por petición: el proxy de controlpanel limita el tamaño de cada petición
      if (config.value.PermitirAdjuntos && adjuntos.value.length && !payload.website) {
        await subirAdjuntos()
        payload.adjuntos = adjuntos.value.map((a) => ({ Documento: a.documento, Id: a.idSubida, Nombre: a.documento, Contenido: a.contenido }))
      }
      const form = new FormData()
      form.append('solicitud', JSON.stringify(payload))
      // Sin cabeceras propias: el CORS de controlpanel no las admite y el navegador bloquearía el envío.
      // La idempotencia va en el cuerpo (claveEnvio).
      return await $fetch<SolicitudWebRespuesta>(
        `${apiSolicitudes(store)}api/SolicitudWeb/Public/${idNotaria.value}`,
        { method: 'POST', body: form }
      )
    } catch (e: any) {
      const estado: number | undefined = e?.response?.status ?? e?.statusCode
      // La API no ha contestado (red, bloqueo del proxy, CORS) o está caída: la solicitud sale por el correo, sin adjuntos
      const sinApi = estado === undefined || estado === 502 || estado === 503 || estado === 504
      if (sinApi && !payload.website) {
        try {
          // El token de reintento no lo ha gastado la API (solo lo usa si reenvía el correo)
          return await enviarPorCorreo(payload, tokenReintento || recaptchaToken)
        } catch (e2) {
          console.error('[SolicitudWeb] Tampoco ha salido por el correo de respaldo', e2)
        }
      }
      const codigo: string = e?.data?.codigo ?? (estado === 429 ? 'LIMITE' : estado === 413 ? 'ADJUNTOS_DEMASIADO_GRANDES' : 'ERROR')
      throw new ErrorEnvioSolicitud(codigo, estado)
    }
  }

  /**
   * Estadísticas del panel (embudo): avisa, sin ningún dato personal, de que alguien ha llegado a un paso.
   * Cada paso se cuenta una vez por solicitud y pestaña. Si falla, no pasa nada.
   */
  function contarPaso(paso: string) {
    if (!import.meta.client || !idWeb.value || !config.value.Activa) return
    const clave = `solicitud-web-pasos-${estado.value.claveEnvio}`
    let hechos: string[] = []
    try { hechos = JSON.parse(sessionStorage.getItem(clave) || '[]') } catch { /* sin almacenamiento */ }
    if (hechos.includes(paso)) return
    hechos.push(paso)
    try { sessionStorage.setItem(clave, JSON.stringify(hechos)) } catch { /* sin almacenamiento */ }
    fetch(`${apiSolicitudes(store)}api/SolicitudWeb/Public/${idWeb.value}/paso`, {
      method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ paso })
    }).catch(() => { /* estadística: no importa si se pierde */ })
  }

  /** Datos de una invitación de la notaría (?inv=), o null si no vale (caducada, usada o inexistente). */
  async function cargarInvitacion(token: string): Promise<null | { tipoOperacion: string; nombre: string; apellidos: string; email: string; telefono: string }> {
    if (!/^[A-Za-z0-9_-]{20,64}$/.test(token) || !idWeb.value) return null
    try {
      return await $fetch(`${apiSolicitudes(store)}api/SolicitudWeb/Public/${idWeb.value}/invitacion/${token}`)
    } catch {
      return null
    }
  }

  function reiniciar() {
    borrarBorrador()
    estado.value = estadoInicial(store.idioma || 'es')
    errores.value = {}
    adjuntos.value = []
    destino.value = null
    destinoElegido.value = false
  }

  return {
    estado,
    errores,
    adjuntos,
    config,
    conNV,
    tipo,
    reglasCita,
    avisoCita,
    citaValida,
    tipos,
    estadoCatalogo,
    cargarCatalogo,
    vigilarIdioma,
    nombreDocumento,
    esProfesional,
    pasos,
    indice,
    pasoActual,
    activarAutoguardado,
    borrarBorrador,
    validarTodo,
    siguiente,
    anterior,
    irAPaso,
    elegirTipo,
    videoconferenciaEn,
    preguntarFirma,
    encargoPlantilla,
    destino,
    destinoElegido,
    elegirDestino,
    anadirInterviniente,
    quitarInterviniente,
    nombreRol,
    limpiarError,
    anadirAdjuntos,
    quitarAdjunto,
    moverAdjunto,
    enviar,
    contarPaso,
    cargarInvitacion,
    reiniciar
  }
}
